import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone,
  AlertTriangle,
  FileText,
  Users,
  Shield,
  Plus,
  Search,
  Filter,
  Zap,
  BookOpen,
  Briefcase,
  Home,
  Heart,
  Brain,
  Calendar,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useModuleLoading } from '../../hooks/useModuleLoading';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';
import { EmergencyPlans } from './EmergencyPlans';
import { QuickActions } from './QuickActions';
import { EmergencyResources } from './EmergencyResources';
import { EmergencyContactsList } from './EmergencyContactsList';
import { EmergencyKit } from './EmergencyKit';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

interface EmergencyCounts {
  plans: number;
  contacts: number;
  packs: number;
  drills: number;
}

export function EmergencyContacts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [emergencyCounts, setEmergencyCounts] = useState<EmergencyCounts>({
    plans: 0,
    contacts: 0,
    packs: 0,
    drills: 0
  });
  
  // Use the new loading hook with optimized settings
  const { loading, error, setLoading, setError, isGloballyLoaded } = useModuleLoading({
    moduleName: 'emergency',
    dependencies: [user?.id],
    skipInitialLoad: true,
    debounceMs: 2000,
    autoRetry: true,
    maxRetries: 2
  });

  const loadEmergencyCounts = async () => {
    if (!user) {
      setError('User not authenticated. Please log in again.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      try {
        // Get active profile first
        const { data: activeProfile } = await supabase
          .from('emergency_profiles')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();

        if (activeProfile) {
          const [
            plansResponse,
            contactsResponse,
            packsResponse,
            drillsResponse
          ] = await Promise.allSettled([
            supabase
              .from('emergency_hazard_plans')
              .select('id', { count: 'exact' })
              .eq('profile_id', activeProfile.id),
            supabase
              .from('emergency_contacts')
              .select('id', { count: 'exact' })
              .eq('profile_id', activeProfile.id),
            supabase
              .from('emergency_packs')
              .select('id', { count: 'exact' })
              .eq('profile_id', activeProfile.id),
            supabase
              .from('emergency_drills')
              .select('id', { count: 'exact' })
              .eq('profile_id', activeProfile.id)
          ]);

          const plans = plansResponse.status === 'fulfilled' && !plansResponse.value.error 
            ? plansResponse.value.count || 0 
            : 0;
          
          const contacts = contactsResponse.status === 'fulfilled' && !contactsResponse.value.error 
            ? contactsResponse.value.count || 0 
            : 0;
          
          const packs = packsResponse.status === 'fulfilled' && !packsResponse.value.error 
            ? packsResponse.value.count || 0 
            : 0;

          const drills = drillsResponse.status === 'fulfilled' && !drillsResponse.value.error 
            ? drillsResponse.value.count || 0 
            : 0;

          setEmergencyCounts({
            plans,
            contacts,
            packs,
            drills
          });
        } else {
          setEmergencyCounts({
            plans: 0,
            contacts: 0,
            packs: 0,
            drills: 0
          });
        }
      } catch (dbError) {
        console.warn('Database queries failed, showing empty counts:', dbError);
        setEmergencyCounts({
          plans: 0,
          contacts: 0,
          packs: 0,
          drills: 0
        });
      }
    } catch (err) {
      console.error('Error in loadEmergencyCounts:', err);
      setEmergencyCounts({
        plans: 0,
        contacts: 0,
        packs: 0,
        drills: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const navItems: NavItem[] = [
    { 
      id: 'plans', 
      label: 'My Plans', 
      icon: <FileText size={24} />,
      description: 'Create and manage emergency plans',
      color: 'bg-blue-50 text-blue-600'
    },
    { 
      id: 'quick-actions', 
      label: 'Quick Actions', 
      icon: <Zap size={24} />,
      description: '1-click emergency actions',
      color: 'bg-red-50 text-red-600'
    },
    {
      id: 'resources', 
      label: 'Resources', 
      icon: <BookOpen size={24} />,
      description: 'Emergency resources and guides',
      color: 'bg-green-50 text-green-600'
    },
    { 
      id: 'contacts', 
      label: 'Contacts', 
      icon: <Users size={24} />,
      description: 'Manage emergency contacts',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      id: 'kit', 
      label: 'Emergency Kit', 
      icon: <Briefcase size={24} />,
      description: 'Emergency preparedness kit',
      color: 'bg-amber-50 text-amber-600'
    }
  ];

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setError('User not authenticated. Please log in again.');
      return;
    }
    
    if (user) {
      loadEmergencyCounts();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => {
      if (user) {
        loadEmergencyCounts();
      }
    };
    window.addEventListener('refresh-emergency', handler);
    return () => window.removeEventListener('refresh-emergency', handler);
  }, [user]);

  const handleNavigation = (id: string) => {
    navigate(`/dashboard/wellbeing/emergency/${id}`);
  };

  const handleCreatePlan = () => {
    navigate('/dashboard/wellbeing/emergency/plans');
  };

  return (
    <Routes>
      <Route path="/" element={
        <div className="p-6 max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Emergency Management</h1>
              <p className="text-gray-600">Plan and prepare for emergencies</p>
            </div>
            <div className="flex gap-4">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search emergency items..."
              />
              <Button
                variant="outline"
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-idecide-primary text-white' : ''}
              >
                All
              </Button>
              <Button
                variant="primary"
                onClick={handleCreatePlan}
                icon={<Plus size={20} />}
              >
                New Plan
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="text-blue-600" size={20} />
                <span className="font-medium text-gray-900">Emergency Plans</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{emergencyCounts.plans}</p>
              <p className="text-sm text-gray-600 mt-1">Active plans</p>
        </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Users className="text-purple-600" size={20} />
                <span className="font-medium text-gray-900">Contacts</span>
                  </div>
              <p className="text-2xl font-bold text-purple-600">{emergencyCounts.contacts}</p>
              <p className="text-sm text-gray-600 mt-1">Emergency contacts</p>
                </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="text-amber-600" size={20} />
                <span className="font-medium text-gray-900">Emergency Packs</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">{emergencyCounts.packs}</p>
              <p className="text-sm text-gray-600 mt-1">Prepared packs</p>
                </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="font-medium text-gray-900">Drills Completed</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{emergencyCounts.drills}</p>
              <p className="text-sm text-gray-600 mt-1">Practice drills</p>
        </div>
      </div>

          {/* Quick Actions */}
          <Card className="mb-6">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard/wellbeing/emergency/plans')}
        >
                  <FileText size={20} className="mr-2" />
                  Create Emergency Plan
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard/wellbeing/emergency/quick-actions')}
                >
                  <Zap size={20} className="mr-2" />
                  Emergency Actions
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard/wellbeing/emergency/contacts')}
                >
                  <Users size={20} className="mr-2" />
                  Add Contact
                </Button>
                <Button 
                  variant="primary" 
                  className="w-full justify-start"
                  onClick={() => window.location.href = 'tel:000'}
                  icon={<Phone size={20} />}
                >
                  Call 000 (Emergency)
                </Button>
              </div>
            </div>
          </Card>

          {/* Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {navItems.map((item) => (
              <motion.div
                key={item.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  className="cursor-pointer h-full"
                  onClick={() => handleNavigation(item.id)}
                >
                  <div className="p-6 space-y-4">
                    <div className={`w-12 h-12 rounded-lg ${item.color} flex items-center justify-center`}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.label}</h3>
                      <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2 mt-6">
              <AlertCircle size={20} />
              <p>{error}</p>
      </div>
          )}
    </div>
      } />
      <Route path="plans/*" element={<EmergencyPlans />} />
      <Route path="quick-actions/*" element={<QuickActions />} />
      <Route path="resources/*" element={<EmergencyResources />} />
      <Route path="contacts/*" element={<EmergencyContactsList />} />
      <Route path="kit/*" element={<EmergencyKit />} />
      <Route path="*" element={<Navigate to="/dashboard/wellbeing/emergency" replace />} />
    </Routes>
  );
} 